import * as Icons from "@nova/component-icons"
import { SidePanelProgressBar, SidePanelStatus } from "@nova/component-side-panel-tab-container"
import { TimeElapsed } from "@nova/component-time-elapsed"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import { memo, useEffect, useState } from "react"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"

// Duration in ms before processing time displays failure message instead.
export const processingTimeout = 24 * 60 * 60 * 1000
export const staleLiveLayerTimeout = 3 * 60 * 60 * 1000

export type StatusAndProgressData =
    | {
          status: undefined
      }
    | {
          status: SidePanelStatus.upload
      }
    | {
          status: SidePanelStatus.empty
      }
    | {
          status: SidePanelStatus.uploading
          completedUploadCount: number
          totalUploadCount: number
          createdAtDate: Date | undefined
      }
    | {
          status: SidePanelStatus.queued
      }
    | {
          status: SidePanelStatus.ready
      }
    | {
          status: SidePanelStatus.loading
          createdAtDate: Date | undefined
          progress: number | undefined
          isPreview?: boolean
      }
    | {
          status: SidePanelStatus.failed
          message?: string
      }
    | {
          status: SidePanelStatus.live
          imageCount: number
          createdBy: { userId: string } | undefined
          createdAtDate: Date | undefined
          isPreview?: boolean
      }
    | {
          status: SidePanelStatus.singleFileUploading
          createdAtDate: Date | undefined
          progress: number | undefined
          totalUploadCount: number | undefined
      }

// This hook causes live layers to show "Nothing Uploaded" after staleLiveLayerTimeout
function useIsStaleLiveLayer(data: StatusAndProgressData): boolean {
    const getIsStale = () =>
        data.status === SidePanelStatus.live &&
        data.imageCount === 0 &&
        !!data.createdAtDate &&
        new Date().getTime() - data.createdAtDate.getTime() >= staleLiveLayerTimeout

    const [isStale, setIsStale] = useState(getIsStale)

    useEffect(() => {
        if (data.status !== SidePanelStatus.live || data.imageCount !== 0 || !data.createdAtDate) {
            setIsStale(false)
            return
        }
        const remaining =
            staleLiveLayerTimeout - (new Date().getTime() - data.createdAtDate.getTime())
        if (remaining <= 0) {
            setIsStale(true)
            return
        }
        setIsStale(false)
        const timer = setTimeout(() => setIsStale(true), remaining)
        return () => clearTimeout(timer)
    }, [data])

    return isStale
}

export function getGenericListItemProgress({
    useStatusAndProgress,
    overrideProcessingText,
    overrideTextColor,
}: {
    useStatusAndProgress: (id: string) => StatusAndProgressData
    overrideProcessingText?: string
    overrideTextColor?: string
}) {
    const GenericListItemProgress = memo(
        function GenericListItemProgress({ id, isVisible }: { id: string; isVisible: boolean }) {
            const { t } = useTranslation()
            const statusAndProgressData = useStatusAndProgress(id)
            const liveCreatedByUserId =
                statusAndProgressData.status === SidePanelStatus.live
                    ? statusAndProgressData.createdBy?.userId
                    : undefined
            const { fullName } = useFullnameForUserId(liveCreatedByUserId)
            const isStaleLiveLayer = useIsStaleLiveLayer(statusAndProgressData)

            if (!statusAndProgressData.status) return null
            return (
                <div
                    className={classNames("status", {
                        "text-gray-200":
                            !overrideTextColor &&
                            isVisible &&
                            statusAndProgressData.status !== SidePanelStatus.failed,
                        "text-gray-0":
                            !overrideTextColor &&
                            isVisible &&
                            statusAndProgressData.status === SidePanelStatus.failed,
                        "!text-[#666666]": !overrideTextColor && !isVisible,
                        [overrideTextColor || ""]: !!overrideTextColor,
                    })}
                >
                    <>
                        {statusAndProgressData.status === SidePanelStatus.upload && (
                            <div className="flex items-center gap-1 mt-2">
                                <Icons.SmallIcon
                                    Icon={Icons.CloudAlert}
                                    className="text-yellow-500"
                                />
                                <small>Not Uploaded</small>
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.empty && (
                            <div className="flex items-center gap-1 mt-2">
                                <Icons.SmallIcon
                                    Icon={Icons.CloudAlert}
                                    className="text-yellow-500"
                                />
                                <small>No Images Staged</small>
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.singleFileUploading && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between">
                                    <small>
                                        {statusAndProgressData.progress === 1
                                            ? "Finalizing"
                                            : "Uploading"}
                                    </small>
                                    {statusAndProgressData.createdAtDate && (
                                        <TimeElapsed
                                            start={statusAndProgressData.createdAtDate}
                                            className="text-[12px]"
                                        />
                                    )}
                                </div>
                                <SidePanelProgressBar
                                    progress={statusAndProgressData.progress || 1}
                                    total={statusAndProgressData.totalUploadCount || 1}
                                    colorHex={"#16C949"}
                                />
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.uploading && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between">
                                    <small>
                                        {`Uploading ${statusAndProgressData.completedUploadCount}/${statusAndProgressData.totalUploadCount}`}
                                    </small>
                                    {statusAndProgressData.createdAtDate && (
                                        <TimeElapsed
                                            start={statusAndProgressData.createdAtDate}
                                            className="text-[12px]"
                                        />
                                    )}
                                </div>
                                <SidePanelProgressBar
                                    progress={statusAndProgressData.completedUploadCount}
                                    total={statusAndProgressData.totalUploadCount}
                                    colorHex={"#16C949"}
                                />
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.queued && (
                            <small className="mt-2">{t(`Upload Queued`)}</small>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.ready && (
                            <small className="mt-2">Ready to Process</small>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.loading && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between">
                                    <small>
                                        {`${overrideProcessingText || "Processing"}: ${
                                            Math.max(
                                                1,
                                                statusAndProgressData.progress || 0
                                            ).toString() + "%"
                                        }`}
                                    </small>
                                    {statusAndProgressData.createdAtDate && (
                                        <TimeElapsed
                                            start={statusAndProgressData.createdAtDate}
                                            className="text-[12px]"
                                        />
                                    )}
                                </div>
                                <SidePanelProgressBar
                                    progress={statusAndProgressData.progress || 1}
                                    total={100}
                                    colorHex={"#27BBFF"}
                                />
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.live && (
                            <div className="flex flex-col gap-2">
                                {statusAndProgressData.imageCount === 0 && !isStaleLiveLayer && (
                                    <small className="text-gray-400">Standby</small>
                                )}
                                {statusAndProgressData.imageCount === 0 && isStaleLiveLayer && (
                                    <small className="text-red-500">Nothing uploaded</small>
                                )}
                                {statusAndProgressData.createdBy && (
                                    <div className="flex gap-2 items-center min-w-0">
                                        <div className="w-[20px] shrink-0 aspect-square rounded-md overflow-hidden">
                                            <ProfilePhoto
                                                userId={statusAndProgressData.createdBy.userId}
                                                fullName={fullName || ""}
                                            />
                                        </div>
                                        <span className="text-[12px] text-gray-200 truncate">
                                            {fullName}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        {statusAndProgressData.status === SidePanelStatus.failed && (
                            <div className="flex items-center gap-1 mt-2">
                                <Icons.SmallIcon
                                    Icon={Icons.ExclamationTriangle}
                                    className="text-red-500"
                                />
                                <small>{statusAndProgressData.message || "Upload Failed"}</small>
                            </div>
                        )}
                    </>
                </div>
            )
        },
        (prevProps, nextProps) => {
            if (prevProps.id !== nextProps.id) return false
            return true
        }
    )
    return GenericListItemProgress
}

export function getBadgeComponent({
    useStatusAndProgress,
}: {
    useStatusAndProgress: (id: string) => StatusAndProgressData
}) {
    return function Badge({ id }: { id: string }) {
        const data = useStatusAndProgress(id)
        const isStaleLiveLayer = useIsStaleLiveLayer(data)

        if (
            (data.status === SidePanelStatus.live || data.status === SidePanelStatus.loading) &&
            data.isPreview
        ) {
            return (
                <div className="bg-[#27BBFF]/10 rounded-3xl px-2 h-5 flex items-center justify-center flex-shrink-0">
                    <span className="font-['IBM_Plex_Mono'] text-[12px] text-[#27BBFF]/80 leading-[140%]">
                        Preview
                    </span>
                </div>
            )
        }

        if (data.status !== SidePanelStatus.live) return null
        if (isStaleLiveLayer) return null
        if (data.imageCount === 0) {
            return (
                <div className="relative h-5 w-5 flex-shrink-0">
                    <div className="absolute">
                        <Icons.SmallIcon Icon={Icons.LiveDrone} className="text-red-500" />
                    </div>
                    <div className="absolute">
                        <Icons.SmallIcon
                            Icon={Icons.LiveDrone}
                            className="animate-ping-slow duration-600 text-red-500 opacity-75"
                        />
                    </div>
                </div>
            )
        }
        return (
            <div className="bg-green-700 rounded-3xl px-2 h-5 flex items-center justify-center flex-shrink-0">
                <span className="font-['IBM_Plex_Mono'] text-[12px] text-white/70 leading-[140%]">
                    {data.imageCount}
                </span>
            </div>
        )
    }
}
